import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { securedAxiosInstance } from '../../api/axios';
import {
  getDocs,
  uploadDocs,
} from '../../stores/slices/resources/docs/docsActions';
import FileUpload from '../FileUpload';
import { FaPlus } from 'react-icons/fa';
import TagModal from './TagModal';
import chroma from 'chroma-js';
import MSelect from 'react-select';
import DropzoneField from '../DropzoneField';

const UploadResource = ({ isOpen, onOpen, onClose, toUpdate }) => {
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [filesBeingUploaded, setFilesBeingUploaded] = useState([]);
  const { userInfo } = useSelector(state => state.user);
  const { activeFolder } = useSelector(state => state.folders);
  const schema = yup
    .object({
      // resourceType: yup.string().required('Resource type is required'),
      attachment: !toUpdate.state
        ? yup.mixed().required('Attachment is required')
        : yup.mixed().notRequired(),
    })
    .required();
  const [tags, setTags] = useState([]);
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    isOpen: isOpenTagCreate,
    onOpen: onOpenTagCreate,
    onClose: onCloseTagCreate,
  } = useDisclosure();

  // for getting tags
  const getTags = async () => {
    setIsLoadingTags(true);
    const res = await securedAxiosInstance.get(`/resource_tags`);
    setTags(
      res.data.map(data => {
        return {
          color: data.colour,
          label: data.name,
          value: data.id,
        };
      })
    );
    setIsLoadingTags(false);
    return;
  };
  useEffect(() => {
    getTags();
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      tagsSelect: [],
    },
  });
  const watchTags = watch('tagsSelect');

  useEffect(() => {
    if (toUpdate.state) {
      reset({
        tagsSelect: tags.filter(tag => {
          return toUpdate.doc.resource_tags.find(toUpdateResource => {
            return toUpdateResource.id === tag.value;
          });
        }),
      });
    }
  }, [tags, toUpdate]);

  const closeTagModal = newTag => {
    if (newTag) {
      setTags(tags => {
        return [...tags, newTag];
      });
      reset({
        tagsSelect: [...watchTags, newTag],
      });
    }
    onCloseTagCreate();
  };

  const uploadFormData = async data => {
    const formData = new FormData();
    const tagsList = watchTags.map(tag => tag.value);
    const file = data.attachment;
    let fileName = '';
    let success = { status: true, msg: '' };
    if (data.name) {
      fileName = data.name;
    } else {
      fileName = file.name;
    }
    formData.append('resource[name]', fileName);
    if (!toUpdate.state || tagsList.length > 0) {
      for (let i = 0; i < tagsList.length; i++) {
        formData.append('resource[resource_tags][]', tagsList[i]);
      }
    } else {
      formData.append('resource[resource_tags][]', null);
    }
    if (file) {
      formData.append('resource[document_data]', file);
      formData.append(
        'resource[extension]',
        file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2)
      );
    }
    formData.append('resource[folder_id]', activeFolder.id);
    formData.append('resource[author_id]', userInfo.id);

    const res = await dispatch(
      uploadDocs({
        formData,
        folderId: 0,
        isUpdate: toUpdate,
      })
    );

    if (res.type === 'docs/uploadDocs/rejected') {
      success = { status: false, msg: res.payload.data.error };
      setFilesBeingUploaded(filesBeingUploaded => {
        return filesBeingUploaded.map(fileUpload => {
          if (fileUpload.file === file) {
            return { ...fileUpload, status: 'error' };
          }
          return fileUpload;
        });
      });
    } else {
      setFilesBeingUploaded(filesBeingUploaded => {
        return filesBeingUploaded.map(fileUpload => {
          if (fileUpload.file === file) {
            return { ...fileUpload, status: 'success' };
          }
          return fileUpload;
        });
      });
    }

    return success;
  };

  const onUpload = async data => {
    const files = data.attachment;
    let uploadState = { status: false, msg: null };
    if (files) {
      setFilesBeingUploaded(
        files.map(file => {
          return { file, status: 'uploading' };
        })
      );
      await Promise.all(
        files.map(async file => {
          uploadState = await uploadFormData({ ...data, attachment: file });
        })
      );
    } else {
      uploadState = await uploadFormData(data);
    }
    if (uploadState.status) {
      await dispatch(getDocs({ id: activeFolder.id }));
      reset();
      onClose();
      toast({
        description: toUpdate.state
          ? 'Resource updated successfully'
          : 'File uploaded successfully',
        colorScheme: 'primary',
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    } else {
      toast({
        title: uploadState.msg || 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };
  return (
    <React.Fragment>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={() => {
          reset();
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(onUpload)}>
            <ModalHeader>
              {toUpdate.state ? 'Edit' : 'Upload'} resource
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={'2'}>
                <FormControl id="name" isInvalid={errors.name}>
                  <Input
                    id="name"
                    {...register('name')}
                    hidden
                    defaultValue={toUpdate.state ? toUpdate.doc.name : ''}
                  />
                  <FormErrorMessage>
                    {errors.name && errors.name.message}
                  </FormErrorMessage>
                </FormControl>

                <Controller
                  control={control}
                  name="tagsSelect"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error },
                  }) => (
                    <FormControl isInvalid={!!error} id="tagsSelect">
                      <FormLabel>Tags</FormLabel>

                      <HStack>
                        <Box w={'full'}>
                          <MSelect
                            isMulti
                            name={name}
                            ref={ref}
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value}
                            options={tags}
                            placeholder="Tags"
                            closeMenuOnSelect={false}
                            isLoading={isLoadingTags}
                            isClearable
                            className="chakra-react-select"
                            classNamePrefix="chakra-react-select"
                            selectedOptionStyle="check"
                            styles={{
                              control: styles => ({
                                ...styles,
                                backgroundColor: 'white',
                                borderColor: 'lightGray.500',
                              }),
                              option: (
                                styles,
                                { data, isDisabled, isFocused, isSelected }
                              ) => {
                                const color = chroma(data.color);
                                return {
                                  ...styles,
                                  backgroundColor: isDisabled
                                    ? undefined
                                    : isSelected
                                    ? data.color
                                    : isFocused
                                    ? color.alpha(0.5).css()
                                    : data.color,
                                  color: isDisabled ? '#ccc' : 'white',
                                  cursor: isDisabled
                                    ? 'not-allowed'
                                    : 'default',
                                  ':active': {
                                    ...styles[':active'],
                                    backgroundColor: !isDisabled
                                      ? isSelected
                                        ? data.color
                                        : data.color
                                      : undefined,
                                  },
                                };
                              },
                              multiValue: (styles, { data }) => {
                                const color = chroma(data.color);
                                return {
                                  ...styles,
                                  backgroundColor: data.color,

                                  borderRadius: '5px',
                                };
                              },
                              multiValueLabel: (styles, { data }) => ({
                                ...styles,
                                color: 'white',
                              }),
                              multiValueRemove: (styles, { data }) => ({
                                ...styles,
                                color: 'white',
                                ':hover': {
                                  backgroundColor: data.color,
                                  color: 'white',
                                  borderRadius: '5px',
                                },
                              }),
                            }}
                          />
                        </Box>
                        <IconButton
                          variant={'outline'}
                          color={'primary'}
                          icon={<FaPlus />}
                          onClick={onOpenTagCreate}
                        />
                      </HStack>
                      <FormErrorMessage>
                        {error && error.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />

                <Controller
                  control={control}
                  name="attachment"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error },
                  }) => (
                    <FormControl isInvalid={errors.attachment} id="attachment">
                      <FormLabel>Attachment</FormLabel>
                      <DropzoneField
                        ref={ref}
                        name={name}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        filesBeingUploaded={filesBeingUploaded}
                        multiple={!toUpdate.state}
                      />
                      <FormErrorMessage>
                        {errors.attachment && errors.attachment.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                mr={3}
                type="submit"
                isLoading={isSubmitting}
                disabled={isLoadingTags}
              >
                Save
              </Button>
              <Button
                onClick={() => {
                  reset();
                  onClose();
                }}
                variant="outline"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      <TagModal isOpen={isOpenTagCreate} onClose={closeTagModal} />
    </React.Fragment>
  );
};

export default UploadResource;
